import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api";

export type GroupSummary = {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  imageUrl: string | null;
  tags: string[];
  createdAt: string;
  eventCount: number;
};

export type GroupEvent = {
  slug: string;
  name: string;
  date: string;
  time: string;
  coverImageUrl: string | null;
  attendeeCount: number;
  interestedCount?: number;
  isPast: boolean;
  venue: { name: string | null; city: string | null };
};

export type PublicGroupResponse = {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  imageUrl: string | null;
  tags: string[];
  ownerName: string;
  isOwner: boolean;
  events: GroupEvent[];
};

export type GroupFormValues = {
  name: string;
  description?: string;
  imageUrl?: string | null;
  tags?: string[];
};

// ---------------------------------------------------------------------------
// Public
// ---------------------------------------------------------------------------

export function useGroup(slug: string | null) {
  return useQuery({
    queryKey: ["group", slug],
    queryFn: async () => api.get<PublicGroupResponse>(`/groups/${slug}`),
    enabled: !!slug,
    retry: false,
  });
}

// ---------------------------------------------------------------------------
// My groups (authenticated)
// ---------------------------------------------------------------------------

export function useMyGroups() {
  return useQuery({
    queryKey: ["myGroups"],
    queryFn: async () => api.get<GroupSummary[]>("/host/groups"),
  });
}

export function useHostGroup(slug: string | null) {
  return useQuery({
    queryKey: ["hostGroup", slug],
    queryFn: async () => api.get<GroupSummary>(`/host/groups/${slug}`),
    enabled: !!slug,
    retry: false,
  });
}

export function useCreateGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: GroupFormValues) =>
      api.post<{ id: string; slug: string }>("/host/groups", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["myGroups"] });
    },
  });
}

export function useUpdateGroup(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: GroupFormValues) =>
      api.patch<{ slug: string }>(`/host/groups/${slug}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["myGroups"] });
      qc.invalidateQueries({ queryKey: ["hostGroup", slug] });
      qc.invalidateQueries({ queryKey: ["group", slug] });
    },
  });
}

export function useDeleteGroup(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => api.delete<unknown>(`/host/groups/${slug}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["myGroups"] });
      qc.removeQueries({ queryKey: ["hostGroup", slug] });
      qc.removeQueries({ queryKey: ["group", slug] });
    },
  });
}
